/*  src/pages/ManagerBoard.jsx  */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Chip,
  Select, MenuItem, FormControl, InputLabel, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  CircularProgress
} from '@mui/material';
import { initKeycloak } from '../keycloak';
import { detachKiosk } from '../utils/kioskApi';
import { setFacilityWeather } from '../utils/kioskPhotoApi';
import FacilityTransferCard from './facilityTransfer/FacilityTransferCard';
import FacilityTransfersPanel from './facilityTransfer/FacilityTransfersPanel';
import LatestValidations from './LatestValidations';
import PendingBadge from './admin/PendingBadge';
import { pendingCounts } from '../utils/adminPendingApi';
import { LocationAlert, LocationCell, useUnlocate } from './locationCheck/LocationCheck';
import { isMislocated } from '../utils/locationCheck';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/* ───────────── Config ───────────── */
const API_BASE_URL   = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const DEFAULT_CENTER = [48.8566, 2.3522];

const PRICING_OPTS = [
  { id: 2, label: 'Standard (1 facility)',   price: 9.99,  color: '#2196f3' },
  { id: 4, label: 'Plus (3 facilities)',     price: 19.99, color: '#ff9800' },
  { id: 3, label: 'Premium (10 facilities)', price: 29.99, color: '#9c27b0' },
];
const PRICE_MAP = Object.fromEntries(PRICING_OPTS.map(p => [p.id, p.price]));

// validation_requests.validation_status values, as they come back from
// GetFacilityScanLog - only 'OK' is a genuinely successful scan.
const SCAN_STATUS_META = {
  OK:             { label: 'OK',                color: 'success' },
  OUT_OF_RANGE:   { label: 'Out of range',       color: 'warning' },
  MOCK_LOCATION:  { label: 'Mock location',      color: 'error' },
  INVALID_SIG:    { label: 'Invalid signature',  color: 'error' },
  REPLAY:         { label: 'Replay',             color: 'error' },
};
const scanStatusMeta = (status) => SCAN_STATUS_META[status] || { label: status || 'Unknown', color: 'default' };

// Best-effort escaping for dropping a value into a double-quoted shell
// argument in the generated command preview - covers the common case
// (a WiFi password/SSID with spaces or a stray quote), not a full
// shell parser. The admin should still eyeball the preview before
// running it, same as with any copy-pasted command.
const shellQuote = (value) => `"${String(value).replace(/(["\\$`])/g, '\\$1')}"`;

const iconEmoji = {
  restaurant:'🍽️', bar:'🍺', gite:'🏡', hotel:'🏨',
  museum:'🏛️', shop:'🛒', pharmacy:'💊', bakery:'🥖',
  activity:'🎯', camping:'🏕️', other:'📍',
};
const getFacilityIcon = (type = 'other') => L.divIcon({
  html:`<span style="font-size:22px;">${iconEmoji[type] ?? iconEmoji.other}</span>`,
  className:'', iconSize:[22,22], iconAnchor:[11,22],
});
const currency = new Intl.NumberFormat('fr-FR', {
  style:'currency',
  currency:'EUR',
});

// Leaflet's bindPopup() renders string content via innerHTML with no
// escaping - facility name/address are owner-supplied free text, so
// building popups as real DOM nodes (textContent, never string HTML)
// is required to avoid stored XSS running in whoever views this map.
function buildValidationPopup(v) {
  const container = document.createElement('div');
  container.style.fontSize = '12px';
  container.appendChild(document.createTextNode(`Facility ID : ${v.storeId ?? '—'}`));
  container.appendChild(document.createElement('br'));
  container.appendChild(document.createTextNode(new Date(v.created_at).toLocaleString()));
  return container;
}

function buildFacilityPopup(f) {
  const container = document.createElement('div');
  const strong = document.createElement('strong');
  strong.textContent = f.name ?? '';
  container.appendChild(strong);
  container.appendChild(document.createElement('br'));
  container.appendChild(document.createTextNode(f.address ?? ''));
  container.appendChild(document.createElement('br'));
  container.appendChild(document.createTextNode(`Type : ${f.facility_type}`));
  return container;
}

/* ───────── Component ───────── */
export default function ManagerBoard() {
  /* refs for Leaflet */
  const validationMapRef = useRef(null);
  const validationMapObj = useRef(null);
  const facilityMapRef   = useRef(null);
  const facilityMapObj   = useRef(null);

  /* auth */
  const [kcInstance, setKcInstance] = useState(null);
  const [token, setToken]           = useState('');
  const [pending, setPending]       = useState(null); // the red badges (routes/adminPending.ts)
  const [forbidden, setForbidden]   = useState(false);
  const loginRequestedRef           = useRef(false);   // prevent multi-login

  /* data state */
  const [facilities,   setFacilities]   = useState([]);
  const [validations,  setValidations]  = useState([]);
  const [buyingPlans,  setBuyingPlans]  = useState({}); // owner_email → planId

  /* ui status */
  const [loading,      setLoading]      = useState(true);
  const [updateStatus, setUpdateStatus] = useState({});
  const [authStatus,   setAuthStatus]   = useState({});
  const [planStatus,   setPlanStatus]   = useState({});
  const [demoStatus,   setDemoStatus]   = useState({});
  const [authFilter,   setAuthFilter]   = useState('all');
  // « Marquer non localisé » (position far from the address, components/locationCheck)
  const unlocate = useUnlocate({ getToken: () => kcInstance?.token ?? token, setFacilities });

  /* rating-kiosk device: display mode, idle message, and the one-time
     key-reveal dialog after a rotate */
  const [qrModeStatus,  setQrModeStatus]  = useState({});
  const [idleMsgStatus, setIdleMsgStatus] = useState({});
  const [keyDialog, setKeyDialog] = useState({ open: false, facilityId: null, key: '' });
  const [weatherStatus, setWeatherStatus] = useState({}); // facility id → 'ok' | 'error'
  const saveFacilityWeather = async (facilityId, weather) => {
    try {
      await setFacilityWeather(facilityId, weather, kcInstance?.token ?? token);
      setFacilities(list => list.map(fi => (fi.id === facilityId ? { ...fi, kiosk_weather: weather } : fi)));
      setWeatherStatus(s => ({ ...s, [facilityId]: 'ok' }));
    } catch {
      setWeatherStatus(s => ({ ...s, [facilityId]: 'error' }));
    }
  };
  const [transferFor, setTransferFor] = useState(null); // facility whose "Transfer" dialog is open
  const kcRef = useRef(null);
  kcRef.current = kcInstance;
  const getTransferToken = useCallback(() => kcRef.current?.token ?? token, [token]);

  /* which QR a facility shows to diners: no-app (default) or app-based -
     orthogonal to qr_display_mode above (that's about how the QR gets
     minted, this is about what kind of QR it is) */
  const [qrAppModeStatus, setQrAppModeStatus] = useState({});
  const [usbCommandForm, setUsbCommandForm] = useState({ ssid: '', wifiPassword: '', port: '' });

  /* per-facility scan log ("Logs" button) */
  const [scanLog, setScanLog] = useState({
    open: false, facilityId: null, facilityName: '', loading: false, error: null, rows: [],
  });

  /* ─── Auth ─── */
useEffect(() => {
  (async () => {
    const kc = await initKeycloak();

    // ✅ FIX 2: clean the URL so we don't keep reusing a URL
    // with Keycloak fragments (?code, #state, etc.)
    window.history.replaceState(
      null,
      '',
      window.location.origin + window.location.pathname
    );

    if (!kc.authenticated) {
      // Avoid double login calls (StrictMode, re-renders, etc.)
      if (loginRequestedRef.current) return;
      loginRequestedRef.current = true;
      kc.login({
        // IMPORTANT: clean redirect URI (no hash / query)
        redirectUri: `${window.location.origin}/ManagerDashboard`,
      });
      return;
    }

    if (!kc.hasRealmRole('ROLE_ADMIN')) {
      // Every route this page calls is already ROLE_ADMIN-gated server
      // side, so this is defense-in-depth, not the actual boundary - but
      // without it any logged-in user renders/fetches this whole admin
      // dashboard before finding out the API calls fail.
      setForbidden(true);
      setLoading(false);
      return;
    }

    setKcInstance(kc);
    setToken(kc.token ?? '');
  })().catch(console.error);
}, []);

  /* ─── What waits behind the Lists and Commercial buttons ─── */
  useEffect(() => {
    if (!token) return;
    pendingCounts(token).then(setPending).catch(() => {});
  }, [token]);

  /* ─── Fetch dashboard data ─── */
  useEffect(() => {
    if (!token) return;

    const H = { Authorization:`Bearer ${token}` };

    Promise.all([
      fetch(`${API_BASE_URL}/ShowAllFacilities`,          { headers: H }).then(r => r.json()),
      fetch(`${API_BASE_URL}/GetAllValidation?limit=500`, { headers: H }).then(r => r.json()),
      fetch(`${API_BASE_URL}/admin/getBuyingPlan`,        { headers: H }).then(r => r.json()),
      fetch(`${API_BASE_URL}/admin/GetAllBuyingPlan`,     { headers: H }).then(r => r.json()),
    ])
      .then(([fac, val, _currentPlan, allPlans]) => {
        setFacilities(Array.isArray(fac) ? fac : []);
        setValidations(Array.isArray(val) ? val : []);

        const map = {};
        if (Array.isArray(allPlans)) {
          allPlans.forEach(p => {
            if (p.owner_email && p.plan_id != null) {
              map[p.owner_email] = p.plan_id;
            }
          });
        }
        setBuyingPlans(map);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  /* ─── Buying-plan upsert ─── */
  const saveBuyingPlan = async (ownerEmail, planId) => {
    if (!token) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/buyingPlan/${encodeURIComponent(ownerEmail)}/${planId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: '{}', // or JSON.stringify({})
        }
      );
      if (!res.ok) throw new Error(res.status);
      setBuyingPlans(bp => ({ ...bp, [ownerEmail]: planId }));
      setPlanStatus(ps => ({ ...ps, [ownerEmail]: 'ok' }));
    } catch (e) {
      console.error('buying-plan error', e);
      setPlanStatus(ps => ({ ...ps, [ownerEmail]: 'error' }));
    } finally {
      setTimeout(
        () => setPlanStatus(ps => ({ ...ps, [ownerEmail]: undefined })),
        2500
      );
    }
  };

  /* ─── End-date & authorization helpers ─── */
  const saveEndDate = async (facilityId, isoDate) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/UpdateEndOfEvaluation/${facilityId}`, {
        method:'PUT',
        headers:{
          'Content-Type':'application/json',
          Authorization:`Bearer ${token}`,
        },
        body: JSON.stringify({ endOfEvaluation: isoDate }),
      });
      if (!res.ok) throw new Error(res.status);
      setFacilities(f =>
        f.map(fi => fi.id === facilityId ? { ...fi, endOfEvaluation: isoDate } : fi)
      );
      setUpdateStatus(s => ({ ...s, [facilityId]: 'ok' }));
    } catch (e) {
      console.error(e);
      setUpdateStatus(s => ({ ...s, [facilityId]: 'error' }));
    } finally {
      setTimeout(
        () => setUpdateStatus(s => ({ ...s, [facilityId]: undefined })),
        3000
      );
    }
  };

  const toggleAuthorization = async (facilityId, current) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/SetIsAuthorized/${facilityId}`, {
        method:'PUT',
        headers:{
          'Content-Type':'application/json',
          Authorization:`Bearer ${token}`,
        },
        body: JSON.stringify({ isAutorized: !current }),
      });
      if (!res.ok) throw new Error(res.status);
      setFacilities(f =>
        f.map(fi => fi.id === facilityId ? { ...fi, isAutorized: !current } : fi)
      );
      setAuthStatus(a => ({ ...a, [facilityId]: 'ok' }));
    } catch (e) {
      console.error(e);
      setAuthStatus(a => ({ ...a, [facilityId]: 'error' }));
    } finally {
      setTimeout(
        () => setAuthStatus(a => ({ ...a, [facilityId]: undefined })),
        3000
      );
    }
  };

  // Demo facilities are exempt from the one-rating-per-device-per-day
  // limit, so the same phone can rate the same facility repeatedly at a
  // trade show or sales demo. Off by default for every facility - only
  // toggle this for a facility that's actually used for demos, never a
  // real customer-facing one.
  // Test mode (kiosk demos at a prospect's): ratings accepted anywhere and repeatedly,
  // kept out of the map, alerts and reports. The demo facility of kiosks not
  // associated yet is picked among these on /admin/kiosk-photos.
  const [testStatus, setTestStatus] = useState({});
  const toggleTestMode = async (facilityId, current) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/kiosk-settings/facility/${facilityId}/test-mode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ testMode: !current }),
      });
      if (!res.ok) throw new Error(res.status);
      setFacilities(f => f.map(fi => fi.id === facilityId ? { ...fi, test_mode: !current ? 1 : 0 } : fi));
      setTestStatus(d => ({ ...d, [facilityId]: 'ok' }));
    } catch (e) {
      console.error(e);
      setTestStatus(d => ({ ...d, [facilityId]: 'error' }));
    } finally {
      setTimeout(() => setTestStatus(d => ({ ...d, [facilityId]: undefined })), 3000);
    }
  };

  const toggleDemoUnrestricted = async (facilityId, current) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/SetDemoUnrestricted/${facilityId}`, {
        method:'PUT',
        headers:{
          'Content-Type':'application/json',
          Authorization:`Bearer ${token}`,
        },
        body: JSON.stringify({ demoUnrestricted: !current }),
      });
      if (!res.ok) throw new Error(res.status);
      setFacilities(f =>
        f.map(fi => fi.id === facilityId ? { ...fi, demo_unrestricted: !current ? 1 : 0 } : fi)
      );
      setDemoStatus(d => ({ ...d, [facilityId]: 'ok' }));
    } catch (e) {
      console.error(e);
      setDemoStatus(d => ({ ...d, [facilityId]: 'error' }));
    } finally {
      setTimeout(
        () => setDemoStatus(d => ({ ...d, [facilityId]: undefined })),
        3000
      );
    }
  };

  // Whether a facility's QR shows via the owner's browser login
  // ('keycloak', default) or a physical rating-kiosk device that mints
  // its own codes with a device key ('device'). Switching to 'device'
  // doesn't generate a key by itself - use "Rotate key" for that.
  const saveQrDisplayMode = async (facilityId, mode) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/SetQrDisplayMode/${facilityId}`, {
        method:'PUT',
        headers:{
          'Content-Type':'application/json',
          Authorization:`Bearer ${token}`,
        },
        body: JSON.stringify({ qrDisplayMode: mode }),
      });
      if (!res.ok) throw new Error(res.status);
      setFacilities(f =>
        f.map(fi => fi.id === facilityId ? { ...fi, qr_display_mode: mode } : fi)
      );
      setQrModeStatus(s => ({ ...s, [facilityId]: 'ok' }));
    } catch (e) {
      console.error(e);
      setQrModeStatus(s => ({ ...s, [facilityId]: 'error' }));
    } finally {
      setTimeout(
        () => setQrModeStatus(s => ({ ...s, [facilityId]: undefined })),
        3000
      );
    }
  };

  // Toggles between the no-app QR (default - diner's own phone camera,
  // no second app) and the app-based QR (requires the YummiTracker
  // companion app to scan). Affects both the web "Afficher le QR" page
  // and the rating-kiosk device flow, since both read qr_app_mode.
  const saveQrAppMode = async (facilityId, mode) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/SetQrAppMode/${facilityId}`, {
        method:'PUT',
        headers:{
          'Content-Type':'application/json',
          Authorization:`Bearer ${token}`,
        },
        body: JSON.stringify({ qrAppMode: mode }),
      });
      if (!res.ok) throw new Error(res.status);
      setFacilities(f =>
        f.map(fi => fi.id === facilityId ? { ...fi, qr_app_mode: mode } : fi)
      );
      setQrAppModeStatus(s => ({ ...s, [facilityId]: 'ok' }));
    } catch (e) {
      console.error(e);
      setQrAppModeStatus(s => ({ ...s, [facilityId]: 'error' }));
    } finally {
      setTimeout(
        () => setQrAppModeStatus(s => ({ ...s, [facilityId]: undefined })),
        3000
      );
    }
  };

  // Generates a fresh device key server-side (only its hash is stored)
  // and shows the plaintext exactly once, in keyDialog - this response
  // is the only place it's ever visible again, so the admin has to copy
  // it into the physical device's provisioning screen right now.
  const rotateDeviceKey = async (facilityId) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/RotateDeviceKey/${facilityId}`, {
        method:'POST',
        headers:{ Authorization:`Bearer ${token}` },
      });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      setKeyDialog({ open: true, facilityId, key: data.deviceKey });
      setUsbCommandForm({ ssid: '', wifiPassword: '', port: '' });
    } catch (e) {
      console.error(e);
      alert('Failed to rotate the device key. Please try again.');
    }
  };

  // "Detach kiosk": erases the facility's device key; its kiosk forgets the
  // facility within ~5 min and shows its association code, to be moved to
  // another facility by scanning it (documentations/kiosk-installation.md).
  const detachFacilityKiosk = async (facilityId) => {
    if (!token) return;
    if (!window.confirm('Detach the kiosk from this facility? It stops working here within ~5 minutes '
      + 'and shows its association code, to be associated with another facility.')) return;
    try {
      await detachKiosk(facilityId, token);
      setFacilities(list => list.map(fi => (fi.id === facilityId ? { ...fi, device_key_hash: null } : fi)));
    } catch (e) {
      console.error(e);
      alert('Failed to detach the kiosk. Please try again.');
    }
  };

  const saveIdleMessage = async (facilityId, message) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/SetDeviceIdleMessage/${facilityId}`, {
        method:'PUT',
        headers:{
          'Content-Type':'application/json',
          Authorization:`Bearer ${token}`,
        },
        body: JSON.stringify({ idleMessage: message }),
      });
      if (!res.ok) throw new Error(res.status);
      setFacilities(f =>
        f.map(fi => fi.id === facilityId ? { ...fi, device_idle_message: message } : fi)
      );
      setIdleMsgStatus(s => ({ ...s, [facilityId]: 'ok' }));
    } catch (e) {
      console.error(e);
      setIdleMsgStatus(s => ({ ...s, [facilityId]: 'error' }));
    } finally {
      setTimeout(
        () => setIdleMsgStatus(s => ({ ...s, [facilityId]: undefined })),
        3000
      );
    }
  };

  // Last 200 scan/validation events for one facility, newest first -
  // good scans and failures (out-of-range, replay, mock location, bad
  // signature) side by side, for a facility the admin is investigating.
  const openScanLog = async (facilityId, facilityName) => {
    setScanLog({ open: true, facilityId, facilityName, loading: true, error: null, rows: [] });
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/GetFacilityScanLog/${facilityId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(res.status);
      const rows = await res.json();
      setScanLog(s => ({ ...s, loading: false, rows: Array.isArray(rows) ? rows : [] }));
    } catch (e) {
      console.error('scan log fetch failed', e);
      setScanLog(s => ({ ...s, loading: false, error: 'Failed to load scan log.' }));
    }
  };

  /* ─── Leaflet maps ─── */
  useEffect(() => {
    if (!validationMapRef.current) return;

    if (!validationMapObj.current) {
      validationMapObj.current = L.map(validationMapRef.current, {
        center: DEFAULT_CENTER,
        zoom: 3,
        scrollWheelZoom: false,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:'&copy; OpenStreetMap contributors',
      }).addTo(validationMapObj.current);
    }

    const map = validationMapObj.current;
    map.eachLayer(l => {
      if (l instanceof L.CircleMarker) map.removeLayer(l);
    });

    validations
      .filter(v => v.scan_lat && v.scan_lon)
      .forEach(v => {
        L.circleMarker([v.scan_lat, v.scan_lon], {
          radius:6,
          weight:1,
          color:'#333',
          fillOpacity:0.85,
        })
          .bindPopup(buildValidationPopup(v))
          .addTo(map);
      });
  }, [validations]);

  useEffect(() => {
    if (!facilityMapRef.current) return;

    if (!facilityMapObj.current) {
      facilityMapObj.current = L.map(facilityMapRef.current, {
        center: DEFAULT_CENTER,
        zoom: 3,
        scrollWheelZoom: false,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:'&copy; OpenStreetMap contributors',
      }).addTo(facilityMapObj.current);
    }

    const map = facilityMapObj.current;
    map.eachLayer(l => {
      if (l instanceof L.Marker) map.removeLayer(l);
    });

    facilities
      .filter(f => f.lat && f.lon)
      .forEach(f => {
        L.marker([f.lat, f.lon], { icon: getFacilityIcon(f.facility_type) })
          .bindPopup(buildFacilityPopup(f))
          .addTo(map);
      });

    if (facilities.length) {
      const latest = facilities[0];
      if (latest.lat && latest.lon) {
        map.flyTo([latest.lat, latest.lon], 14, { duration: 1.2 });
      }
    }
  }, [facilities]);

  /* ─── Derived revenue numbers ─── */
  const currentMonthlyRevenue = Object.values(buyingPlans)
    .reduce((sum, id) => sum + (PRICE_MAP[id] || 0), 0);
  const currentYearlyRevenue  = currentMonthlyRevenue * 12;

  const validatedCount      = facilities.filter(f => f.isAutorized).length;
  const maxMonthlyPerOwner  = PRICE_MAP[3];           // Premium plan (highest tier)
  const potentialAnnualGain = validatedCount * maxMonthlyPerOwner * 12;

  const filteredFacilities = authFilter === 'all'
    ? facilities
    : authFilter === 'mislocated'
      ? facilities.filter(isMislocated)
      : facilities.filter(f => Boolean(f.isAutorized) === (authFilter === 'authorized'));

  if (forbidden) {
    return (
      <Typography sx={{ mt:4, textAlign:'center' }}>
        Accès refusé.
      </Typography>
    );
  }

  /* ─── Loading splash ─── */
  if (loading) {
    return (
      <Typography sx={{ mt:4, textAlign:'center' }}>
        Chargement…
      </Typography>
    );
  }

  /* ─── Render ─── */
  return (
    <Container maxWidth="lg">
      {/* header */}
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:3 }}>
        <Typography variant="h4">Manager Dashboard</Typography>
        <Box>
          <PendingBadge count={pending?.commercial?.total} sx={{ mr:1 }}>
            <Button variant="outlined" component={RouterLink} to="/admin/commercial">
              Commercial Dashboard
            </Button>
          </PendingBadge>
          <Button
            variant="outlined"
            component={RouterLink}
            to="/admin/kiosk-photos"
            sx={{ mr:1 }}
          >
            Kiosk photos
          </Button>
          <PendingBadge count={pending?.lists?.total} sx={{ mr:1 }}>
            <Button variant="outlined" component={RouterLink} to="/admin/lists">
              Lists
            </Button>
          </PendingBadge>
          <Button
            variant="outlined"
            component={RouterLink}
            to="/admin/landings"
            sx={{ mr:1 }}
          >
            Landings
          </Button>
          <Button
            variant="outlined"
            onClick={() => kcInstance?.logout({ redirectUri: `${window.location.origin}/` })}
            sx={{ mr:1 }}
          >
            Logout
          </Button>
          <Button variant="contained" href="/v1/form/">
            Create a Facility
          </Button>
        </Box>
      </Box>

      {/* filter */}
      <FormControl sx={{ mb:2, minWidth:200 }}>
        <InputLabel>Filter by authorization</InputLabel>
        <Select
          value={authFilter}
          onChange={e => setAuthFilter(e.target.value)}
          label="Filter by authorization"
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="authorized">Authorized</MenuItem>
          <MenuItem value="unauthorized">Unauthorized</MenuItem>
          <MenuItem value="mislocated">Localisation suspecte</MenuItem>
        </Select>
      </FormControl>

      {unlocate.notice}
      <LocationAlert facilities={facilities} onUnlocate={unlocate.open} />
      {unlocate.dialog}

      {/* finance widget */}
      <Paper elevation={3} sx={{ p:3, mb:4 }}>
        <Typography variant="h6" gutterBottom>Revenue overview</Typography>
        <Typography>
          Current yearly revenue :
          {' '}
          <b>{currency.format(currentYearlyRevenue)}</b>
        </Typography>
        <Typography sx={{ mb:1 }}>
          Owners on Business plan would yield :
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight:'bold' }}>
          Potential yearly revenue : {currency.format(potentialAnnualGain)}
        </Typography>
      </Paper>

      <LatestValidations validations={validations} />

      {/* facilities table */}
      <Paper elevation={3} sx={{ p:3, mb:4 }}>
        <Typography variant="h6" gutterBottom>Facilities</Typography>
        <Box sx={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#f5f5f5' }}>
                {[
                  'ID','Name','Adresse ↔ position','Owner','Plan','Created',
                  'Status','Amount','End of eval.','Save','Authorized','Demo / Test',
                  'QR Display','QR Mode','Logs','Commercial',
                ].map(h => (
                  <th key={h} style={{ padding:8, textAlign:'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredFacilities.map(f => {
                const isoDate = f.endOfEvaluation?.slice(0,10) ?? '';
                const owner   = f.owner_email;
                const planId  = buyingPlans[owner] ?? '';

                return (
                  <tr
                    key={f.id}
                    style={{
                      borderTop:'1px solid #e0e0e0',
                      backgroundColor: f.isAutorized ? '#e3fbe3' : undefined,
                    }}
                  >
                    <td style={{ padding:8 }}>{f.id}</td>
                    <td style={{ padding:8 }}>{f.name}</td>
                    <td style={{ padding:8 }}><LocationCell facility={f} onUnlocate={unlocate.open} /></td>
                    <td style={{ padding:8 }}>
                      {owner}
                      <Button size="small" sx={{ ml: 0.5, minWidth: 0 }} onClick={() => setTransferFor(f)}>Transfer</Button>
                      {f.plan_name === 'Premium' && (
                        <Chip
                          label="Premium"
                          size="small"
                          color="secondary"
                          sx={{ ml:1, fontSize:'0.7rem', height:'20px' }}
                        />
                      )}
                    </td>

                    <td style={{ padding:8 }}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                          value={
                            planId ||
                            PRICING_OPTS.find(p => p.label.startsWith(f.plan_name))?.id ||
                            ''
                          }
                          displayEmpty
                          onChange={e => saveBuyingPlan(owner, parseInt(e.target.value, 10))}
                        >
                          <MenuItem value="">– choose –</MenuItem>
                          {PRICING_OPTS.map(p => (
                            <MenuItem key={p.id} value={p.id}>
                              <span style={{ color: p.color }}>
                                {p.label} – {p.price.toFixed(2)} €
                              </span>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {planStatus[owner] === 'ok'    && (
                        <Chip size="small" label="✓" color="success" sx={{ ml:1 }} />
                      )}
                      {planStatus[owner] === 'error' && (
                        <Chip size="small" label="✕" color="error" sx={{ ml:1 }} />
                      )}
                    </td>

                    <td style={{ padding:8 }}>
                      {new Date(f.created_at).toLocaleDateString()}
                    </td>

                    <td
                      style={{
                        padding:8,
                        color: f.sales_payment_status === 'paid' ? 'green' : 'red',
                        fontWeight:'bold',
                      }}
                    >
                      {f.sales_payment_status}
                    </td>

                    <td style={{ padding:8 }}>
                      {f.sales_total} €
                    </td>

                    <td style={{ padding:8 }}>
                      <input
                        type="date"
                        defaultValue={isoDate}
                        onBlur={e => {
                          const v = e.target.value;
                          if (v && v !== isoDate) saveEndDate(f.id, v);
                        }}
                      />
                      {updateStatus[f.id] === 'ok' && (
                        <Chip size="small" label="✓" color="success" sx={{ ml:1 }} />
                      )}
                      {updateStatus[f.id] === 'error' && (
                        <Chip size="small" label="✕" color="error" sx={{ ml:1 }} />
                      )}
                    </td>

                    <td style={{ padding:8 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => saveEndDate(
                          f.id,
                          document.querySelector(`input[type="date"][value="${isoDate}"]`)?.value || isoDate
                        )}
                      >
                        Save
                      </Button>
                    </td>

                    <td style={{ padding:8 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => toggleAuthorization(f.id, f.isAutorized)}
                      >
                        {f.isAutorized ? 'Revoke' : 'Authorize'}
                      </Button>
                      {authStatus[f.id] === 'ok' && (
                        <Chip size="small" label="✓" color="success" sx={{ ml:1 }} />
                      )}
                      {authStatus[f.id] === 'error' && (
                        <Chip size="small" label="✕" color="error" sx={{ ml:1 }} />
                      )}
                    </td>

                    <td style={{ padding:8 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color={f.demo_unrestricted ? 'warning' : 'inherit'}
                        onClick={() => toggleDemoUnrestricted(f.id, f.demo_unrestricted)}
                      >
                        {f.demo_unrestricted ? 'Demo: ON' : 'Demo: off'}
                      </Button>
                      {demoStatus[f.id] === 'ok' && (
                        <Chip size="small" label="✓" color="success" sx={{ ml:1 }} />
                      )}
                      {demoStatus[f.id] === 'error' && (
                        <Chip size="small" label="✕" color="error" sx={{ ml:1 }} />
                      )}
                      <Box sx={{ mt: 0.5 }}>
                        <Button
                          size="small"
                          variant={f.test_mode ? 'contained' : 'outlined'}
                          color={f.test_mode ? 'secondary' : 'inherit'}
                          onClick={() => toggleTestMode(f.id, f.test_mode)}
                          title="Notes acceptées partout, hors carte, alertes et rapports (démos des bornes)"
                        >
                          {f.test_mode ? 'Test: ON' : 'Test: off'}
                        </Button>
                        {testStatus[f.id] === 'ok' && <Chip size="small" label="✓" color="success" sx={{ ml:1 }} />}
                        {testStatus[f.id] === 'error' && <Chip size="small" label="✕" color="error" sx={{ ml:1 }} />}
                      </Box>
                    </td>

                    <td style={{ padding:8, minWidth:220 }}>
                      <Box sx={{ display:'flex', flexDirection:'column', gap:0.75 }}>
                        <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                          <FormControl size="small" sx={{ minWidth:140 }}>
                            <Select
                              value={f.qr_display_mode || 'keycloak'}
                              onChange={e => saveQrDisplayMode(f.id, e.target.value)}
                            >
                              <MenuItem value="keycloak">Keycloak (default)</MenuItem>
                              <MenuItem value="device">Device</MenuItem>
                            </Select>
                          </FormControl>
                          {qrModeStatus[f.id] === 'ok' && (
                            <Chip size="small" label="✓" color="success" />
                          )}
                          {qrModeStatus[f.id] === 'error' && (
                            <Chip size="small" label="✕" color="error" />
                          )}
                        </Box>

                        {f.qr_display_mode === 'device' && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => rotateDeviceKey(f.id)}
                            >
                              {f.device_key_hash ? 'Rotate device key' : 'Generate device key'}
                            </Button>
                            {f.device_key_hash && (
                              <Button size="small" variant="outlined" color="error" onClick={() => detachFacilityKiosk(f.id)}>
                                Detach kiosk
                              </Button>
                            )}

                            <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                              <TextField
                                size="small"
                                placeholder="Idle screen message"
                                defaultValue={f.device_idle_message || ''}
                                onBlur={e => {
                                  const v = e.target.value;
                                  if (v !== (f.device_idle_message || '')) saveIdleMessage(f.id, v);
                                }}
                              />
                              {idleMsgStatus[f.id] === 'ok' && (
                                <Chip size="small" label="✓" color="success" />
                              )}
                              {idleMsgStatus[f.id] === 'error' && (
                                <Chip size="small" label="✕" color="error" />
                              )}
                            </Box>
                            {/* Weather on the home screen: follow the global setting (/admin/kiosk-photos) or force it. */}
                            <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                              <TextField
                                select
                                size="small"
                                label="Weather"
                                value={f.kiosk_weather || 'DEFAULT'}
                                onChange={e => saveFacilityWeather(f.id, e.target.value)}
                                sx={{ minWidth: 150 }}
                              >
                                <MenuItem value="DEFAULT">Default</MenuItem>
                                <MenuItem value="ON">On</MenuItem>
                                <MenuItem value="OFF">Off</MenuItem>
                              </TextField>
                              {weatherStatus[f.id] === 'ok' && <Chip size="small" label="✓" color="success" />}
                              {weatherStatus[f.id] === 'error' && <Chip size="small" label="✕" color="error" />}
                            </Box>
                          </>
                        )}
                      </Box>
                    </td>

                    <td style={{ padding:8 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => saveQrAppMode(f.id, f.qr_app_mode === 'app' ? 'no_app' : 'app')}
                      >
                        {f.qr_app_mode === 'app' ? '📱 App' : '🌐 No app'}
                      </Button>
                      {qrAppModeStatus[f.id] === 'ok' && (
                        <Chip size="small" label="✓" color="success" sx={{ ml:1 }} />
                      )}
                      {qrAppModeStatus[f.id] === 'error' && (
                        <Chip size="small" label="✕" color="error" sx={{ ml:1 }} />
                      )}
                    </td>

                    <td style={{ padding:8 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openScanLog(f.id, f.name)}
                      >
                        Logs
                      </Button>
                    </td>

                    <td style={{ padding:8 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        component={RouterLink}
                        to={`/admin/commercial/facility/${f.id}`}
                      >
                        Fiche
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </Paper>

      {token && <FacilityTransfersPanel getToken={getTransferToken} />}

      {/* buying plans summary */}
      <Paper elevation={3} sx={{ p:3, mb:4 }}>
        <Typography variant="h6" gutterBottom>All Buying Plans</Typography>
        <Box sx={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#f5f5f5' }}>
                <th style={{ padding:8 }}>Owner</th>
                <th style={{ padding:8 }}>Plan</th>
                <th style={{ padding:8 }}>End of Plan</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(buyingPlans).map(([email, planId]) => {
                const plan = PRICING_OPTS.find(p => p.id === planId);
                return (
                  <tr key={email} style={{ borderTop:'1px solid #e0e0e0' }}>
                    <td style={{ padding:8 }}>{email}</td>
                    <td style={{ padding:8, color: plan?.color }}>
                      {plan?.label ?? 'Unknown'}
                    </td>
                    <td style={{ padding:8 }}>—</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </Paper>

      {/* maps */}
      <Paper elevation={3} sx={{ p:3, mb:4 }}>
        <Typography variant="h6" gutterBottom>
          All facilities (last added centred)
        </Typography>
        <div ref={facilityMapRef} style={{ height:400, width:'100%' }} />
      </Paper>

      <Paper elevation={3} sx={{ p:3 }}>
        <Typography variant="h6" gutterBottom>
          Geolocated validations
        </Typography>
        <div ref={validationMapRef} style={{ height:400, width:'100%' }} />
      </Paper>

      <Box sx={{ textAlign:'right', mt:2 }}>
        <Button
          variant="outlined"
          onClick={() => window.scrollTo({ top:0, behavior:'smooth' })}
        >
          Back to top
        </Button>
      </Box>

      {/* "Transfer": invite the facility's real owner (utils/facilityTransfer.ts). */}
      <Dialog open={!!transferFor} onClose={() => setTransferFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer — {transferFor?.name} (#{transferFor?.id})</DialogTitle>
        <DialogContent>
          {transferFor && (
            <FacilityTransferCard facilityId={transferFor.id} ownerEmail={transferFor.owner_email}
              getToken={() => kcInstance?.token ?? token} />
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setTransferFor(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Device key reveal - shown exactly once, right after generation.
          The backend only ever stores a hash of this value, so closing
          this dialog without copying the key means flashing the device
          again after another rotation. */}
      {(() => {
        const usbProvisionCommand = [
          'python3 tools/provision_over_usb.py',
          usbCommandForm.port || '/dev/cu.usbserial-XXXX',
          '--ssid', shellQuote(usbCommandForm.ssid),
          '--pass', shellQuote(usbCommandForm.wifiPassword),
          '--facility-id', String(keyDialog.facilityId ?? ''),
          '--device-key', shellQuote(keyDialog.key),
        ].join(' ');

        return (
      <Dialog
        open={keyDialog.open}
        onClose={() => setKeyDialog({ open:false, facilityId:null, key:'' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Device key — facility #{keyDialog.facilityId}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb:2 }}>
            This key is shown only once. Copy it into the kiosk's WiFi/device
            provisioning screen now — it cannot be retrieved again after you
            close this dialog, only rotated (which invalidates it).
          </Alert>
          <TextField
            fullWidth
            value={keyDialog.key}
            InputProps={{ readOnly: true }}
            onFocus={e => e.target.select()}
          />

          <Typography variant="subtitle1" sx={{ mt:3, mb:1 }}>
            Build the USB provisioning command
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb:2 }}>
            Fills in <code>tools/provision_over_usb.py</code> with this key and
            facility ID, so a commercial rep can pre-provision the kiosk from a
            laptop before it ships — see the firmware README for the full flow.
          </Typography>

          <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
            <TextField
              label="Venue WiFi SSID"
              size="small"
              fullWidth
              value={usbCommandForm.ssid}
              onChange={e => setUsbCommandForm(f => ({ ...f, ssid: e.target.value }))}
            />
            <TextField
              label="Venue WiFi password"
              size="small"
              fullWidth
              type="text"
              value={usbCommandForm.wifiPassword}
              onChange={e => setUsbCommandForm(f => ({ ...f, wifiPassword: e.target.value }))}
            />
            <TextField
              label="Serial port"
              size="small"
              fullWidth
              placeholder="/dev/cu.usbserial-XXXX"
              helperText="On a Mac: run `ls /dev/cu.*` before/after plugging the kiosk in via its UART0 USB-C port — the new entry is the port."
              value={usbCommandForm.port}
              onChange={e => setUsbCommandForm(f => ({ ...f, port: e.target.value }))}
            />
          </Box>

          <TextField
            fullWidth
            multiline
            minRows={2}
            sx={{ mt:2, '& textarea': { fontFamily:'monospace', fontSize:'0.8rem' } }}
            InputProps={{ readOnly: true }}
            onFocus={e => e.target.select()}
            value={usbProvisionCommand}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              navigator.clipboard?.writeText(usbProvisionCommand).catch(() => {});
            }}
          >
            Copy command
          </Button>
          <Button
            onClick={() => {
              navigator.clipboard?.writeText(keyDialog.key).catch(() => {});
            }}
          >
            Copy key only
          </Button>
          <Button
            variant="contained"
            onClick={() => setKeyDialog({ open:false, facilityId:null, key:'' })}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
        );
      })()}

      {/* Per-facility scan log - last 200 validation attempts, newest
          first, good and failed side by side. */}
      <Dialog
        open={scanLog.open}
        onClose={() => setScanLog(s => ({ ...s, open:false }))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Scan log — {scanLog.facilityName} (#{scanLog.facilityId})
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh' }}>
          {scanLog.loading && (
            <Box sx={{ display:'flex', justifyContent:'center', py:4 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {scanLog.error && (
            <Alert severity="error">{scanLog.error}</Alert>
          )}
          {!scanLog.loading && !scanLog.error && scanLog.rows.length === 0 && (
            <Typography color="text.secondary">No scans recorded for this facility yet.</Typography>
          )}
          {!scanLog.loading && !scanLog.error && scanLog.rows.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb:1 }}>
                Showing the last {scanLog.rows.length} event{scanLog.rows.length === 1 ? '' : 's'}, newest first.
              </Typography>
              <Box sx={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.85rem' }}>
                  <thead>
                    <tr style={{ background:'#f5f5f5' }}>
                      {['Time','Status','Rating','Device','Country','Location'].map(h => (
                        <th key={h} style={{ padding:6, textAlign:'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {scanLog.rows.map(r => {
                      const meta = scanStatusMeta(r.validation_status);
                      return (
                        <tr key={r.id ?? r.validation_id} style={{ borderTop:'1px solid #e0e0e0' }}>
                          <td style={{ padding:6, whiteSpace:'nowrap' }}>
                            {new Date(r.created_at).toLocaleString()}
                          </td>
                          <td style={{ padding:6 }}>
                            <Chip size="small" label={meta.label} color={meta.color} />
                          </td>
                          <td style={{ padding:6 }}>{r.rating ?? '—'}</td>
                          <td style={{ padding:6 }}>{r.device_id || '—'}</td>
                          <td style={{ padding:6 }}>{r.countryCode || '—'}</td>
                          <td style={{ padding:6, whiteSpace:'nowrap' }}>
                            {r.scan_lat != null && r.scan_lon != null
                              ? `${Number(r.scan_lat).toFixed(4)}, ${Number(r.scan_lon).toFixed(4)}`
                              : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScanLog(s => ({ ...s, open:false }))}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
